import { Container, BottomCTA, LinkButton } from '@hg/stylizator/components'
import { Text } from '@hg/stylizator/typography'
import { desktop } from '@hg/stylizator/utils'
import * as React from 'react'
import styled from 'styled-components'
import theme from 'app/styles/theme'

import Post from 'app/components/blog/Post'
import PromoBannerWithImage from 'app/components/blog/PromoBanner/PromoBannerWithImage'
import SearchAndCategories from 'app/components/blog/SearchAndCategories'
import Separator from 'app/components/blog/Separator'
import Topic from 'app/components/blog/Topic'
import ClientOnly from 'app/components/ClientOnly'
import { usePromoBannerData } from 'app/hooks/use-promo-banner-data'
import { useTranslation } from 'app/hooks/use-translation'
import SingleColumnLayout from 'app/layouts/SingleColumnLayout'
import { TPageData } from 'app/service/api/theme'
import { GIFT_COUPON_URL } from 'app/parameters'

interface IProps {
  pageData: TPageData<'post-home'>
}

const PostHomePage: React.FC<IProps> = ({ pageData }) => {
  const t = useTranslation()
  const promoBannerData = usePromoBannerData()
  const [firstTopic, ...otherTopics] = pageData.topics

  return (
    <SingleColumnLayout>
      <Background>
        <Container
          margins={{
            mobile: 0,
            tablet: 0,
            desktop: 0,
            largeDesktop: 0,
          }}
          style={{ padding: '0 16px', boxSizing: 'border-box' }}
        >
          <BlogLayoutTop>
            <SearchAndCategories categories={pageData.categories} currentCategory={{ name: 'All' }} />

            <StickyAndRecentPosts>
              <PostSpacing>
                <Post post={pageData.sticky} variant="large" />
              </PostSpacing>

              <Separator />

              <RecentPosts>
                <Text type="heading_medium" useCompact>
                  <h2>{t('recent_articles')}</h2>
                </Text>

                <RecentPostList>
                  {pageData.recent.map((post) => (
                    <Post key={post.permalink} post={post} variant="xsmall" />
                  ))}
                </RecentPostList>
              </RecentPosts>
            </StickyAndRecentPosts>

            <StickyAndTopicSeparator />

            <Topic topic={firstTopic} />
          </BlogLayoutTop>
        </Container>

        <PromoBannerSpacing>
          <ClientOnly>
            <PromoBannerWithImage buttonClassName="target-blog-join-button" {...promoBannerData} />
          </ClientOnly>
        </PromoBannerSpacing>

        <Container
          margins={{
            mobile: 0,
            tablet: 0,
            desktop: 0,
            largeDesktop: 0,
          }}
          style={{ padding: '0 16px', boxSizing: 'border-box' }}
        >
          <BlogLayoutBottom>
            {otherTopics.map((topic, index) => (
              <Topic topic={topic} key={topic.url} withSeparator={index < otherTopics.length - 1} />
            ))}
          </BlogLayoutBottom>
        </Container>
      </Background>

      <BottomCTA
        showImage={false}
        button={
          <LinkButton className="target-blog-get-started" type="primary" size="large" href={GIFT_COUPON_URL}>
            <Text type="label" color={theme.color.white}>
              <span>{t('get_started')}</span>
            </Text>
          </LinkButton>
        }
        title={t('ready_to_make_sweet_money')}
        subtitle={t('join_today_and_earn_sweet_money')}
      />
    </SingleColumnLayout>
  )
}

const Background = styled.div`
  background: ${(props) => props.theme.color.blue[5]};
`

const BlogLayoutTop = styled.div`
  padding: 40px 0 0;

  @media ${desktop} {
    padding: 56px 0 0;
  }
`

const BlogLayoutBottom = styled.div`
  padding: 0 0 40px;

  @media ${desktop} {
    padding: 0 0 112px;
  }
`

const StickyAndRecentPosts = styled.div`
  display: grid;
  margin-top: 40px;
  row-gap: 40px;
  grid-template-columns: 1fr;
  grid-template-areas:
    'post'
    'line'
    'recent';

  & > hr {
    grid-area: line;
  }

  @media ${desktop} {
    margin: 56px 0 64px;
    gap: 64px;
    grid-template-columns: 60% 1fr;
    grid-template-areas:
      'post recent'
      'line line';
  }
`

const StickyAndTopicSeparator = styled(Separator)`
  margin: 40px 0;

  @media ${desktop} {
    display: none;
  }
`

const PostSpacing = styled.div`
  grid-area: post;
`

const RecentPosts = styled.div`
  grid-area: recent;
`

const RecentPostList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  margin-top: 24px;

  @media ${desktop} {
    margin-top: 32px;
  }
`

const PromoBannerSpacing = styled.div`
  margin: 40px 0;

  @media ${desktop} {
    margin: 72px 0;
  }
`

export default PostHomePage
